import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { isNoldDomain } from "../lib/domain";

// Composant SEO réutilisable — injecte title / meta description / OpenGraph / canonical
// et un éventuel bloc JSON-LD (Schema.org). Compatible SPA (react-helmet-async).
//
// Utilisation :
//   <SEOHead
//     title="Ma page"
//     description="Description courte (~150 caractères)"
//     canonical="/ma-page"           // relatif ou absolu
//     image="/og-image.jpg"          // og:image
//     type="website"                 // og:type
//     jsonLd={{ ... }}               // objet Schema.org sérialisé automatiquement
//   />
export default function SEOHead({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPreview = /emergentagent\.com$/i.test(host) || /\.emergent\.host$/i.test(host) || /emergent(?:cf)?\.cloud$/i.test(host);
  // Alignement avec /lib/domain.js — les previews Nold Go (`noldgo-*.preview.emergentagent.com`)
  // renvoient bien noldgo.com comme base canonique, pour cohérence des URL absolues.
  const base = isNoldDomain() ? "https://www.noldgo.com" : "https://www.bentskingo.com";

  // Canonical peut être relatif ou absolu
  const canonicalUrl = canonical
    ? (canonical.startsWith("http") ? canonical : base + canonical)
    : (typeof window !== "undefined" ? base + window.location.pathname : base + "/");

  // OG image absolue
  const ogImage = image
    ? (image.startsWith("http") ? image : base + image)
    : base + "/og-image.jpg";

  const robots = (isPreview || noindex) ? "noindex, nofollow" : "index, follow";

  // Nettoyage : react-helmet-async n'écrase pas les balises statiques posées dans index.html.
  // Sur les pages qui montent SEOHead, on retire les doublons pour laisser Helmet gérer.
  // Les balises Helmet portent un attribut `data-rh` ; on retire uniquement celles sans.
  useEffect(() => {
    const removeDuplicates = () => {
      const namedSelectors = [
        'meta[name="description"]',
        'meta[name="robots"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'meta[name="twitter:image"]',
        'meta[name="twitter:card"]',
      ];
      const propSelectors = [
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[property="og:url"]',
        'meta[property="og:image"]',
        'meta[property="og:type"]',
      ];
      const linkSelectors = ['link[rel="canonical"]'];

      [...namedSelectors, ...propSelectors, ...linkSelectors].forEach((sel) => {
        const nodes = document.querySelectorAll(sel);
        // Garde uniquement les balises gérées par Helmet (data-rh) — supprime les statiques.
        nodes.forEach((n) => {
          if (!n.hasAttribute("data-rh") && !n.getAttribute("data-react-helmet")) {
            n.remove();
          }
        });
      });
    };
    // Exécuter après le rendu Helmet initial.
    removeDuplicates();
    const t = setTimeout(removeDuplicates, 50);
    return () => clearTimeout(t);
  }, [title, description, canonicalUrl, ogImage]);

  return (
    <Helmet prioritizeSeoTags>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} data-rh="true" />}
      <meta name="robots" content={robots} data-rh="true" />
      <link rel="canonical" href={canonicalUrl} data-rh="true" />
      {title && <meta property="og:title" content={title} data-rh="true" />}
      {description && <meta property="og:description" content={description} data-rh="true" />}
      <meta property="og:type" content={type} data-rh="true" />
      <meta property="og:url" content={canonicalUrl} data-rh="true" />
      <meta property="og:image" content={ogImage} data-rh="true" />
      {title && <meta name="twitter:title" content={title} data-rh="true" />}
      {description && <meta name="twitter:description" content={description} data-rh="true" />}
      <meta name="twitter:image" content={ogImage} data-rh="true" />
      <meta name="twitter:card" content="summary_large_image" data-rh="true" />
      {jsonLd && (
        <script type="application/ld+json" data-rh="true">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
